import { Component, OnInit, HostListener, NgZone, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

interface Country { code: string; flag: string; name: string; }
interface Activity { title: string; transportMode: string; cost: number | null; }
interface DayPlan { title: string; activities: Activity[]; }
interface Hotel { name: string; category: string; place: string; checkIn: string; checkOut: string; rooms: number | null; extraBeds: number | null; cost: number | null; services: string; }
interface BookingRecord { id: number; fullName: string; destination: string; documentId: string; arrivalDate: string; departureDate: string; phone: string; }

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {

  // ─── AUTH STATE ───────────────────────────────────────────────
  isAuthenticated = false;
  showSignIn = false;

  signInEmail = '';
  signInPassword = '';
  signInError = '';
  isSigningIn = false;
  showPassword = false;

  // ─── APP STATE ────────────────────────────────────────────────
  showForm = false;
  showDashboard = false;
  pendingLoginAction: 'dashboard' | 'submit' | null = null;
  pendingSubmit = false;
  dashboardBookings: BookingRecord[] = [];
  isDashboardLoading = false;
  isLoading = false;
  showToast = false;
  showPdfPopup = false;
  isPdfLoading = false;
  pdfLoadingType: 'with' | 'without' | null = null;
  pendingDocumentId: string | null = null;

  downloadedWith = false;
  downloadedWithout = false;

  // ─── EDIT MODE STATE ────────────────────────────────────────────
  editingBookingId: number | null = null;
  isLoadingBookingForEdit = false;
  lastActionWasEdit = false;

  // ─── SOFT DELETE STATE ────────────────────────────────────────
  confirmDeleteId: number | null = null;
  confirmDeleteName: string = '';
  isDeleting = false;

  // ─── PDF VIEWER STATE (kept for template compatibility, no longer used to show a preview) ─
  showPdfViewer = false;
  pdfViewerUrl: SafeResourceUrl | null = null;
  pdfViewerTitle = '';
  isDashboardPdfLoading = false;

  // ─── DASHBOARD PDF DOWNLOAD STATE (per-row loading indicator) ──
  downloadingDocId: string | null = null;
  downloadingType: 'with' | 'without' | null = null;

  today = new Date().toISOString().split('T')[0];

  // ─── SEARCH ───────────────────────────────────────────────────
  searchQuery = '';

  get filteredBookings(): BookingRecord[] {
    if (!this.searchQuery.trim()) return this.dashboardBookings;
    const q = this.searchQuery.trim().toLowerCase();
    return this.dashboardBookings.filter(b =>
      b.fullName.toLowerCase().includes(q) ||
      b.phone.toLowerCase().includes(q)
    );
  }

  onSearchChange() {
    this.currentPage = 1;
    this.cdr.detectChanges();
  }

  clearSearch() {
    this.searchQuery = '';
    this.currentPage = 1;
    this.cdr.detectChanges();
  }

  // ─── PAGINATION ───────────────────────────────────────────────
  currentPage = 1;
  pageSize = 10;

  get totalPages(): number {
    return Math.ceil(this.filteredBookings.length / this.pageSize);
  }

  get pagedBookings(): BookingRecord[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredBookings.slice(start, start + this.pageSize);
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.cdr.detectChanges();
    }
  }

  // ─── PDF FILENAME FORMAT ────────────────────────────────────────
  // Shamudri_Tourism_Itinerary_STL000245_John_Smith_Dubai.pdf
  private toTitleCase(value: string): string {
    return (value || '')
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('_');
  }

  private formatDocumentCode(documentId: string | number): string {
    const digitsOnly = String(documentId).replace(/\D/g, '');
    const padded = digitsOnly.padStart(6, '0');
    return `STL${padded}`;
  }

  generatePdfFileName(documentId: string, fullName: string, destination: string): string {
    const code = this.formatDocumentCode(documentId);
    const safeName = this.toTitleCase(fullName) || 'Guest';
    const safeDestination = this.toTitleCase(destination) || 'Trip';
    return `Shamudri_Tourism_Itinerary_${code}_${safeName}_${safeDestination}.pdf`;
  }

  // Form Fields
  fullName = '';
  email = '';
  passport = 'India';
  phoneNumber = '';
  destination = '';
  adultsCount: number | null = null;
  childrenBelow12: number | null = null;
  arrivalDate = '';
  departureDate = '';
  durationDays = 0;
  specialRequests = '';

  // Data Arrays
  itinerary: DayPlan[] = [];
  hotels: Hotel[] = [{ name: '', category: '', place: '', checkIn: '', checkOut: '', rooms: 1, extraBeds: null, cost: null, services: '' }];

  // Country Dropdown
  showDropdown = false;
  countrySearch = '';
  selectedCountry: Country = { code: "+91", flag: "🇮🇳", name: "India" };
  allCountries: Country[] = [
    { code: "+93", flag: "🇦🇫", name: "Afghanistan" }, { code: "+355", flag: "🇦🇱", name: "Albania" },
    { code: "+213", flag: "🇩🇿", name: "Algeria" }, { code: "+61", flag: "🇦🇺", name: "Australia" },
    { code: "+43", flag: "🇦🇹", name: "Austria" }, { code: "+880", flag: "🇧🇩", name: "Bangladesh" },
    { code: "+32", flag: "🇧🇪", name: "Belgium" }, { code: "+55", flag: "🇧🇷", name: "Brazil" },
    { code: "+1", flag: "🇨🇦", name: "Canada" }, { code: "+86", flag: "🇨🇳", name: "China" },
    { code: "+20", flag: "🇪🇬", name: "Egypt" }, { code: "+33", flag: "🇫🇷", name: "France" },
    { code: "+49", flag: "🇩🇪", name: "Germany" }, { code: "+91", flag: "🇮🇳", name: "India" },
    { code: "+62", flag: "🇮🇩", name: "Indonesia" }, { code: "+39", flag: "🇮🇹", name: "Italy" },
    { code: "+81", flag: "🇯🇵", name: "Japan" }, { code: "+60", flag: "🇲🇾", name: "Malaysia" },
    { code: "+52", flag: "🇲🇽", name: "Mexico" }, { code: "+31", flag: "🇳🇱", name: "Netherlands" },
    { code: "+64", flag: "🇳🇿", name: "New Zealand" }, { code: "+92", flag: "🇵🇰", name: "Pakistan" },
    { code: "+63", flag: "🇵🇭", name: "Philippines" }, { code: "+7", flag: "🇷🇺", name: "Russia" },
    { code: "+966", flag: "🇸🇦", name: "Saudi Arabia" }, { code: "+65", flag: "🇸🇬", name: "Singapore" },
    { code: "+27", flag: "🇿🇦", name: "South Africa" }, { code: "+82", flag: "🇰🇷", name: "South Korea" },
    { code: "+34", flag: "🇪🇸", name: "Spain" }, { code: "+94", flag: "🇱🇰", name: "Sri Lanka" },
    { code: "+46", flag: "🇸🇪", name: "Sweden" }, { code: "+41", flag: "🇨🇭", name: "Switzerland" },
    { code: "+66", flag: "🇹🇭", name: "Thailand" }, { code: "+90", flag: "🇹🇷", name: "Turkey" },
    { code: "+971", flag: "🇦🇪", name: "UAE" }, { code: "+44", flag: "🇬🇧", name: "United Kingdom" },
    { code: "+1", flag: "🇺🇸", name: "USA" }
  ];
  filteredCountries: Country[] = [];

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    this.filteredCountries = [...this.allCountries];
    this.showSignIn = false;
    const token = localStorage.getItem('auth_token');
    if (token) {
      this.isAuthenticated = true;
      if (localStorage.getItem('show_dashboard') === 'true') {
        this.loadDashboard();
      }
    }
  }

  // ─── SIGN IN ──────────────────────────────────────────────────
  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  signIn() {
    this.signInError = '';
    if (!this.signInEmail.trim() || !this.signInPassword.trim()) {
      this.signInError = 'Please enter your username and password.';
      return;
    }
    this.isSigningIn = true;
    this.http.post<any>('https://test.staybit.online/itinerary/api/auth/login', {
   // this.http.post<any>('http://localhost:8010/itinerary/api/auth/login', {
      username: this.signInEmail.trim(),
      password: this.signInPassword
    }).subscribe({
      next: (res) => {
        this.ngZone.run(() => {
          this.isSigningIn = false;
          const token = res?.token || res?.accessToken || 'authenticated';
          localStorage.setItem('auth_token', token);
          this.isAuthenticated = true;
          this.showSignIn = false;
          this.pendingLoginAction = null;
          this.loadDashboard();
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          this.isSigningIn = false;
          if (err.status === 401 || err.status === 403) {
            this.signInError = 'Invalid username or password. Please try again.';
          } else if (err.status === 0) {
            this.signInError = 'Unable to connect. Please check your internet connection.';
          } else {
            this.signInError = err?.error?.message || 'Sign in failed. Please try again.';
          }
          this.cdr.detectChanges();
        });
      }
    });
  }

  // ─── SIGN OUT ─────────────────────────────────────────────────
  signOut() {
    const token = localStorage.getItem('auth_token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('show_dashboard');
    this.isAuthenticated = false;
    this.showSignIn = false;
    this.showForm = false;
    this.showDashboard = false;
    this.dashboardBookings = [];
    this.pendingLoginAction = null;
    this.signInEmail = '';
    this.signInPassword = '';
    this.signInError = '';
    this.cdr.detectChanges();
    if (token) {
      this.http.post(
        //'http://localhost:8010/itinerary/api/auth/logout',
         'https://test.staybit.online/itinerary/api/auth/logout',
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      ).subscribe({
        next: () => console.log('Token successfully invalidated on server.'),
        error: (err) => console.warn('Server logout notice (token may already be expired):', err)
      });
    }
  }

  // ─── NAVIGATION ───────────────────────────────────────────────
  openForm() {
    this.editingBookingId = null;
    this.resetForm();
    this.showForm = true;
    this.showDashboard = false;
    this.showSignIn = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  goToDashboard() {
    if (this.isAuthenticated) {
      this.loadDashboard();
    } else {
      this.pendingLoginAction = 'dashboard';
      this.showSignIn = true;
    }
  }

  loadDashboard() {
    this.showDashboard = true;
    this.showForm = false;
    this.showSignIn = false;
    localStorage.setItem('show_dashboard', 'true');
    this.isDashboardLoading = true;
    this.dashboardBookings = [];
    this.currentPage = 1;
    this.searchQuery = '';
    const token = localStorage.getItem('auth_token');
    this.http.get<any[]>(
       'https://test.staybit.online/itinerary/api/bookings',
      //'http://localhost:8010/itinerary/api/bookings',
      { headers: { 'Authorization': `Bearer ${token}` } }
    )
    .subscribe({
      next: (res) => {
        this.ngZone.run(() => {
          if (res.length > 0) console.log('API booking sample:', JSON.stringify(res[0]));
          this.dashboardBookings = res.map((b: any) => ({
            id: b.id,
            fullName: b.fullName || 'N/A',
            destination: b.destination || 'N/A',
            documentId: String(b.documentId || b.id),
            arrivalDate: this.parseDate(b.arrivalDate),
            departureDate: this.parseDate(b.departureDate),
            phone: b.phone || ''
          })).sort((a, b) => b.id - a.id);
          this.isDashboardLoading = false;
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          this.isDashboardLoading = false;
          console.warn('Dashboard load error:', err);
          this.cdr.detectChanges();
        });
      }
    });
  }

  private parseDate(val: any): string {
    if (val === null || val === undefined) return '';
    if (Array.isArray(val) && val.length >= 3) {
      const [y, m, d] = val;
      return `${String(d).padStart(2,'0')}/${String(m).padStart(2,'0')}/${y}`;
    }
    const s = String(val).trim();
    if (!s || s === 'null' || s === 'undefined') return '';
    const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[3]}/${match[2]}/${match[1]}`;
    }
    return s;
  }

  // ─── EDIT BOOKING ───────────────────────────────────────────────
  editBooking(bookingId: number) {
    if (this.isLoadingBookingForEdit) return;
    this.isLoadingBookingForEdit = true;
    const token = localStorage.getItem('auth_token');
    this.http.get<any>(
       `https://test.staybit.online/itinerary/api/bookings/${bookingId}`,
      //`http://localhost:8010/itinerary/api/bookings/${bookingId}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    )
    .subscribe({
      next: (b) => {
        this.ngZone.run(() => {
          this.editingBookingId = bookingId;

          this.fullName = b.fullName || '';
          this.email = b.email || '';
          this.passport = b.passport || 'India';

          const phoneStr = (b.phone || '').trim();
          const firstSpaceIdx = phoneStr.indexOf(' ');
          const code = firstSpaceIdx > -1 ? phoneStr.substring(0, firstSpaceIdx) : phoneStr;
          const number = firstSpaceIdx > -1 ? phoneStr.substring(firstSpaceIdx + 1) : '';
          const matchedCountry = this.allCountries.find(c => c.code === code);
          this.selectedCountry = matchedCountry || this.selectedCountry;
          this.phoneNumber = number;

          this.destination = b.destination || '';
          this.arrivalDate = b.arrivalDate || '';
          this.departureDate = b.departureDate || '';
          this.durationDays = b.durationDays || 0;
          this.adultsCount = b.adults ?? null;
          this.childrenBelow12 = b.childrenBelow12 ?? null;
          this.specialRequests = b.specialRequests || '';

          this.hotels = (b.hotels || []).map((h: any) => ({
            name: h.name || '',
            category: h.category || '',
            place: h.place || '',
            checkIn: h.checkIn || '',
            checkOut: h.checkOut || '',
            rooms: h.rooms ?? 1,
            extraBeds: h.extraBeds ?? 0,
            cost: h.cost ?? null,
            services: h.services || ''
          }));
          if (this.hotels.length === 0) {
            this.hotels = [{ name: '', category: '', place: '', checkIn: '', checkOut: '', rooms: 1, extraBeds: null, cost: null, services: '' }];
          }

          this.itinerary = (b.itinerary || []).map((d: any) => ({
            title: d.dayTitle || '',
            activities: [{
              title: d.activities || '',
              transportMode: d.transportMode || '',
              cost: d.transportCost ?? null
            }]
          }));

          this.isLoadingBookingForEdit = false;
          this.showForm = true;
          this.showDashboard = false;
          this.showSignIn = false;
          window.scrollTo({ top: 0, behavior: 'smooth' });
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          this.isLoadingBookingForEdit = false;
          console.warn('Load booking for edit error:', err);
          this.cdr.detectChanges();
          alert('Failed to load booking for editing. Please try again.');
        });
      }
    });
  }

  cancelEdit() {
    this.editingBookingId = null;
    this.resetForm();
    this.loadDashboard();
  }

  // ─── SOFT DELETE ────────────────────────────────────────────────
  requestDelete(booking: BookingRecord) {
    if (this.isDeleting) return;
    this.confirmDeleteId = booking.id;
    this.confirmDeleteName = booking.fullName;
    this.cdr.detectChanges();
  }

  cancelDelete() {
    if (this.isDeleting) return;
    this.confirmDeleteId = null;
    this.confirmDeleteName = '';
    this.cdr.detectChanges();
  }

  confirmDelete() {
    if (this.confirmDeleteId === null || this.isDeleting) return;
    const id = this.confirmDeleteId;
    this.isDeleting = true;
    this.cdr.detectChanges();

    const token = localStorage.getItem('auth_token');
    this.http.delete<any>(
       `https://test.staybit.online/itinerary/api/bookings/${id}`,
     // `http://localhost:8010/itinerary/api/bookings/${id}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    ).subscribe({
      next: () => {
        this.ngZone.run(() => {
          this.dashboardBookings = this.dashboardBookings.filter(b => b.id !== id);
          this.isDeleting = false;
          this.confirmDeleteId = null;
          this.confirmDeleteName = '';

          if (this.pagedBookings.length === 0 && this.currentPage > 1) {
            this.currentPage--;
          }
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          this.isDeleting = false;
          console.warn('Delete booking error:', err);
          this.cdr.detectChanges();
          alert('Failed to delete booking. Please try again.');
        });
      }
    });
  }

  // ─── DASHBOARD PDF DOWNLOAD (direct download — works on mobile & desktop) ─
  downloadDashboardPdf(documentId: string, withCost: boolean) {
    if (this.downloadingDocId === documentId && this.downloadingType === (withCost ? 'with' : 'without')) {
      return; // already downloading this one
    }

    this.downloadingDocId = documentId;
    this.downloadingType = withCost ? 'with' : 'without';
    this.cdr.detectChanges();

     const endpoint = withCost
      ? `https://test.staybit.online/itinerary/api/documents/download/${documentId}/with-cost`
      : `https://test.staybit.online/itinerary/api/documents/download/${documentId}/without-cost`;
    //const endpoint = withCost
      //? `http://localhost:8010/itinerary/api/documents/download/${documentId}/with-cost`
      //: `http://localhost:8010/itinerary/api/documents/download/${documentId}/without-cost`;
    const token = localStorage.getItem('auth_token') ?? '';

    // Look up the booking so we can build the required filename format even
    // if the server's Content-Disposition header is missing or different.
    const booking = this.dashboardBookings.find(b => b.documentId === documentId);
    const fallbackFilename = this.generatePdfFileName(
      documentId,
      booking?.fullName || '',
      booking?.destination || ''
    );

    fetch(endpoint, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Always use our own required naming format instead of trusting
        // the server's Content-Disposition filename.
        link.download = fallbackFilename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);

        this.ngZone.run(() => {
          this.downloadingDocId = null;
          this.downloadingType = null;
          this.cdr.detectChanges();
        });
      })
      .catch(() => {
        this.ngZone.run(() => {
          this.downloadingDocId = null;
          this.downloadingType = null;
          this.cdr.detectChanges();
          alert('PDF download failed. Please try again.');
        });
      });
  }

  downloadViewerPdf() {
    // Retained for template compatibility; no longer used since the preview
    // modal is no longer triggered. Left as a no-op safe fallback.
  }

  closePdfViewer() {
    this.showPdfViewer = false;
    this.pdfViewerUrl = null;
    this.isDashboardPdfLoading = false;
    this.cdr.detectChanges();
  }

  // ─── COUNTRY DROPDOWN ─────────────────────────────────────────
  toggleDropdown(event: Event) {
    event.stopPropagation();
    this.showDropdown = !this.showDropdown;
    if (this.showDropdown) {
      this.countrySearch = '';
      this.filterCountries();
    }
  }

  closeDropdownExplicit() {
    this.showDropdown = false;
    this.cdr.detectChanges();
  }

  filterCountries() {
    const q = this.countrySearch.toLowerCase();
    this.filteredCountries = this.allCountries.filter(c =>
      c.name.toLowerCase().includes(q) || c.code.includes(q)
    );
  }

  selectCountry(country: Country) {
    this.selectedCountry = country;
    this.showDropdown = false;
    this.cdr.detectChanges();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.cc-dropdown')) {
      this.showDropdown = false;
    }
  }

  // ─── DATE & SLIDER LOGIC ──────────────────────────────────────
  onArrivalChange() {
    if (this.arrivalDate && this.arrivalDate < this.today) {
      alert("Arrival date cannot be in the past.");
      this.arrivalDate = '';
      this.departureDate = '';
      return;
    }
    if (this.arrivalDate && this.hotels.length > 0) {
      this.hotels[0].checkIn = this.arrivalDate;
    }
    this.syncDepartureFromSlider();
    if (this.departureDate) this.syncSliderFromDates();
  }

  onDepartureChange() {
    if (!this.arrivalDate) {
      alert("Please select arrival date first.");
      this.departureDate = '';
      return;
    }
    if (this.departureDate <= this.arrivalDate) {
      alert("Departure date must be after arrival date.");
      this.departureDate = '';
      return;
    }
    if (this.departureDate && this.hotels.length > 0) {
      this.hotels[0].checkOut = this.departureDate;
    }
    this.syncSliderFromDates();
  }

  onSliderChange() {
    this.generateItinerary();
    if (this.arrivalDate && this.durationDays > 0) {
      this.syncDepartureFromSlider();
    }
  }

  syncDepartureFromSlider() {
    if (!this.arrivalDate || this.durationDays <= 0) return;
    const start = new Date(this.arrivalDate);
    start.setDate(start.getDate() + Number(this.durationDays));
    this.departureDate = start.toISOString().split('T')[0];
    if (this.hotels.length > 0) {
      this.hotels[0].checkOut = this.departureDate;
    }
  }

  syncSliderFromDates() {
    if (this.arrivalDate && this.departureDate) {
      const a = new Date(this.arrivalDate);
      const d = new Date(this.departureDate);
      const nights = Math.round((d.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
      if (nights >= 1 && nights <= 30) {
        this.durationDays = nights;
        this.generateItinerary();
      }
    }
  }

  // ─── ITINERARY ────────────────────────────────────────────────
  generateItinerary() {
    const currentLength = this.itinerary.length;
    if (this.durationDays > currentLength) {
      for (let i = currentLength; i < this.durationDays; i++) {
        this.itinerary.push({ title: '', activities: [] });
      }
    } else if (this.durationDays < currentLength) {
      this.itinerary.splice(this.durationDays);
    }
  }

  addActivity(dayIndex: number) {
    this.itinerary[dayIndex].activities.push({ title: '', transportMode: '', cost: null });
  }

  removeActivity(dayIndex: number, activityIndex: number) {
    this.itinerary[dayIndex].activities.splice(activityIndex, 1);
  }

  // ─── HOTELS ───────────────────────────────────────────────────
  addHotel() {
    this.hotels.push({ name: '', category: '', place: '', checkIn: '', checkOut: '', rooms: 1, extraBeds: 0, cost: null, services: '' });
  }

  removeHotel(index: number) {
    if (this.hotels.length > 1) {
      this.hotels.splice(index, 1);
    }
  }

  onHotelCheckInChange(hotel: Hotel) {
    if (hotel.checkIn && hotel.checkIn < this.today) {
      alert("Check-in date cannot be in the past.");
      hotel.checkIn = '';
    }
    if (hotel.checkIn && hotel.checkOut && hotel.checkOut <= hotel.checkIn) {
      alert("Check-out date must be after check-in date.");
      hotel.checkOut = '';
    }
  }

  onHotelCheckOutChange(hotel: Hotel) {
    if (!hotel.checkIn) {
      alert("Please select a check-in date first.");
      hotel.checkOut = '';
      return;
    }
    if (hotel.checkOut <= hotel.checkIn) {
      alert("Check-out date must be after check-in date.");
      hotel.checkOut = '';
    }
  }

  // ─── PRICING ──────────────────────────────────────────────────
  get travelCost(): number {
    return this.itinerary.reduce((acc, day) => acc + day.activities.reduce((a, act) => a + (act.cost || 0), 0), 0);
  }

  get hotelCost(): number {
    return this.hotels.reduce((acc, h) => acc + (h.cost || 0), 0);
  }

  get overallPrice(): number {
    return this.travelCost + this.hotelCost;
  }

  // ─── RESET FORM ───────────────────────────────────────────────
  private resetForm() {
    this.fullName = '';
    this.email = '';
    this.passport = 'India';
    this.phoneNumber = '';
    this.destination = '';
    this.adultsCount = null;
    this.childrenBelow12 = null;
    this.arrivalDate = '';
    this.departureDate = '';
    this.durationDays = 0;
    this.specialRequests = '';
    this.itinerary = [];
    this.hotels = [{ name: '', category: '', place: '', checkIn: '', checkOut: '', rooms: 1, extraBeds: null, cost: null, services: '' }];
    this.selectedCountry = { code: "+91", flag: "🇮🇳", name: "India" };
    this.downloadedWith = false;
    this.downloadedWithout = false;
    this.pendingDocumentId = null;
  }

  private showSuccessToastAndGoHome() {
    const wasEdit = this.lastActionWasEdit;
    this.showForm = false;
    this.showToast = true;
    this.editingBookingId = null;
    this.lastActionWasEdit = false;
    this.resetForm();
    if (wasEdit) {
      this.loadDashboard();
    }
    this.cdr.detectChanges();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.showToast = false;
      this.cdr.detectChanges();
    }, 3000);
  }

  closePdfPopup() {
    this.showPdfPopup = false;
    this.showSuccessToastAndGoHome();
  }

  // ─── SUBMIT (create or update, depending on editingBookingId) ──
  submitForm() {
    if (this.arrivalDate && this.arrivalDate < this.today) {
      alert("Arrival date cannot be in the past.");
      return;
    }
    if (this.arrivalDate && this.departureDate && this.departureDate <= this.arrivalDate) {
      alert("Departure date must be after arrival date.");
      return;
    }
    this.isLoading = true;
    const payload = {
      fullName: this.fullName,
      email: this.email,
      passport: this.passport,
      phone: `${this.selectedCountry.code} ${this.phoneNumber}`,
      destination: this.destination,
      arrivalDate: this.arrivalDate,
      departureDate: this.departureDate,
      durationDays: this.durationDays,
      adults: this.adultsCount || 0,
      childrenBelow12: this.childrenBelow12 || 0,
      travelCost: this.travelCost,
      hotelCost: this.hotelCost,
      overallPrice: this.overallPrice,
      specialRequests: this.specialRequests,
      transport: {
        transportName: "DXB Airport Taxi",
        transportType: "Private",
        vehicleType: "Sedan"
      },
      hotels: this.hotels.map(h => ({
        hotelName: h.name,
        category: h.category,
        place: h.place,
        checkIn: h.checkIn,
        checkOut: h.checkOut,
        rooms: h.rooms || 0,
        extraBeds: h.extraBeds || 0,
        cost: h.cost || 0,
        services: h.services || ''
      })),
      itinerary: this.itinerary.map((day, index) => {
        const principalMode = day.activities.find(a => a.transportMode)?.transportMode || "Private Transport Mode";
        const sumCost = day.activities.reduce((sum, a) => sum + (a.cost || 0), 0);
        return {
          dayNumber: index + 1,
          dayTitle: day.title || `Day ${index + 1}`,
          transportMode: principalMode,
          transportCost: sumCost,
          activities: day.activities.map(a => a.title).join(', ') || 'Rest & leisure.'
        };
      })
    };
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) { headers['Authorization'] = `Bearer ${token}`; }

    if (this.editingBookingId) {
      this.lastActionWasEdit = true;
      this.http.put<any>(
        // `https://test.staybit.online/itinerary/api/bookings/${this.editingBookingId}`,
        `http://localhost:8010/itinerary/api/bookings/${this.editingBookingId}`,
        payload,
        { headers }
      ).subscribe({
        next: (res) => {
          this.ngZone.run(() => {
            this.isLoading = false;
            this.pendingDocumentId = res.documentId ? String(res.documentId) : null;
            this.downloadedWith = false;
            this.downloadedWithout = false;
            this.showPdfPopup = true;
            this.cdr.detectChanges();
          });
        },
        error: (err) => {
          console.warn("Booking Update Error:", err);
          this.ngZone.run(() => {
            this.isLoading = false;
            this.cdr.detectChanges();
            alert("An error occurred while updating the booking. Please try again.");
          });
        }
      });
    } else {
      // this.http.post<any>('https://test.staybit.online/itinerary/api/bookings', payload, { headers })
      this.http.post<any>('http://localhost:8010/itinerary/api/bookings', payload, { headers })
      .subscribe({
        next: (res) => {
          this.ngZone.run(() => {
            this.isLoading = false;
            this.pendingDocumentId = res.documentId ? String(res.documentId) : null;
            this.downloadedWith = false;
            this.downloadedWithout = false;
            this.showPdfPopup = true;
            this.cdr.detectChanges();
          });
        },
        error: (err) => {
          console.warn("Booking Process Error:", err);
          this.ngZone.run(() => {
            this.isLoading = false;
            this.cdr.detectChanges();
            alert("An error occurred during booking. Please try again.");
          });
        }
      });
    };
  }

  // ─── PDF DOWNLOAD ─────────────────────────────────────────────
  downloadPdf(withCost: boolean) {
    if (!this.pendingDocumentId) {
      this.showPdfPopup = false;
      this.showSuccessToastAndGoHome();
      return;
    }
    if (this.isPdfLoading) return;
    this.isPdfLoading = true;
    this.pdfLoadingType = withCost ? 'with' : 'without';
    this.cdr.detectChanges();
    //const endpoint = withCost
    //    ? `https://test.staybit.online/itinerary/api/documents/download/${this.pendingDocumentId}/with-cost`
    //    : `https://test.staybit.online/itinerary/api/documents/download/${this.pendingDocumentId}/without-cost`;
    const endpoint = withCost
       ? `http://localhost:8010/itinerary/api/documents/download/${this.pendingDocumentId}/with-cost`
       : `http://localhost:8010/itinerary/api/documents/download/${this.pendingDocumentId}/without-cost`;
    this.http.get(endpoint, { responseType: 'blob' }).subscribe({
      next: (blobRes) => {
        const blob = new Blob([blobRes], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Use the standard naming format instead of the old
        // itinerary_with_cost_<id>.pdf pattern.
        link.download = this.generatePdfFileName(
          this.pendingDocumentId!,
          this.fullName,
          this.destination
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.ngZone.run(() => {
          if (withCost) this.downloadedWith = true;
          else this.downloadedWithout = true;
          this.isPdfLoading = false;
          this.pdfLoadingType = null;
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        console.warn("PDF Download Error:", err);
        this.ngZone.run(() => {
          this.isPdfLoading = false;
          this.pdfLoadingType = null;
          this.cdr.detectChanges();
          alert("PDF download failed. Please try again.");
        });
      }
    });
  }
}